import {
    chatWithAssistant,
    compareBiasMethods,
    detectBiasAPI,
    explainBiasAPI,
    visualizeBiasBertAPI,
    visualizeBiasBertPredictionAPI,
    withCache,
} from '@/utils';

import {
    BiasAnalysis,
    BiasExplanation,
    BiasedWord,
    ComparisonExplanations,
    Token,
} from '@/types/common';
import { BIAS_REASONS } from '@/config/app';

export async function detectBias(
    text: string,
    sensitivity: number,
    bypassCache = false
): Promise<BiasAnalysis> {
    const params = { text, sensitivity };

    return withCache<BiasAnalysis>('detectBias', params, async () => {
        const result = await detectBiasAPI(params);
        const tokens: Token[] = result.tokens;
        const biasedWords: BiasedWord[] = [];
        const labeledText: Record<string, string[]> = {};

        tokens.forEach((token, index) => {
            if (token.labels.length > 0) {
                const labels = token.labels
                    .map((label) => label.split('-')[1])
                    .filter(Boolean);
                const reasons = labels.map((label) => BIAS_REASONS[label]);

                biasedWords.push({
                    word: token.token,
                    reasons,
                    index,
                    labels,
                });

                labels.forEach((label) => {
                    if (!labeledText[label]) {
                        labeledText[label] = [token.token];
                    } else {
                        labeledText[label].push(token.token);
                    }
                });
            }
        });

        return { biasedWords, labeledText, prediction: result };
    });
}

export async function generateExplanation(
    text: string,
    sensitivity: number,
    bypassCache = false
): Promise<BiasExplanation> {
    const params = { text, sensitivity };

    return withCache<BiasExplanation>('generateExplanation', params, () =>
        explainBiasAPI(params)
    );
}

export async function getBertVisualization(
    text: string,
    sensitivity: number,
    bypassCache = false
): Promise<any> {
    const params = { text, sensitivity };

    return withCache<any>('getBertVisualization', params, () =>
        visualizeBiasBertAPI(params)
    );
}

export async function getBertPredictionVisualization(
    text: string,
    sensitivity: number,
    bypassCache = false
): Promise<any> {
    const params = { text, sensitivity };

    return withCache<any>('getBertPredictionVisualization', params, () =>
        visualizeBiasBertPredictionAPI(params)
    );
}

export async function chatWithAI(
    messages: any[],
    model: string,
    bypassCache = false
): Promise<any> {
    const params = {
        messages,
        model,
        user_id: String(crypto.randomUUID()),
    };

    return withCache<any>('chatWithAI', params, () =>
        chatWithAssistant(params)
    );
}

export async function generateExplainableComparisons(
    text: string,
    sensitivity: number,
    prediction: any,
    bypassCache = false
): Promise<ComparisonExplanations> {
    const methods = [
        'lime',
        'shap',
        'integrated_gradients',
        'integrated_gap_gradients',
        'deeplift',
        'attention',
        'lrp',
        'saliency',
        'occlusion',
    ];

    console.log('Invoking: ', process.env.NODE_ENV);

    if (
        process.env.NODE_ENV === 'development' ||
        process.env.NODE_ENV === 'production'
    ) {
        const params = { text, sensitivity, methods };

        return withCache<ComparisonExplanations>(
            'generateExplainableComparisons',
            params,
            () => compareBiasMethods(params)
        );
    } else {
        const params = { text, sensitivity };
        const cacheKey = { text, sensitivity, methods };

        return withCache<ComparisonExplanations>(
            'generateExplainableComparisons',
            cacheKey,
            async () => {
                // First, gather explanations for each method individually
                const explanations = await Promise.all(
                    methods.map(async (method) => {
                        const methodParams = {
                            ...params,
                            method,
                            use_input: true,
                            ner_prediction: prediction,
                        };
                        const explanation = await explainBiasAPI(methodParams);
                        return { method, explanation };
                    })
                );

                // Then call compareBiasMethods with the pre-computed explanations
                const result = await compareBiasMethods({
                    ...params,
                    methods,
                    use_input: true,
                    ner_prediction: prediction,
                    explanations: Object.fromEntries(
                        explanations.map(({ method, explanation }) => [
                            method,
                            explanation.explanation,
                        ])
                    ),
                });

                return {
                    explanations: Object.fromEntries(
                        explanations.map(({ method, explanation }) => [
                            method,
                            explanation,
                        ])
                    ),
                    comparison: result.comparison,
                    prediction: result.prediction,
                };
            }
        );
    }
}
